import { useState, useEffect } from 'react';
import { Group, Loader, Table } from '@mantine/core';
import Header from '../../components/util/header';
import { useSelector } from 'react-redux';
import {
  fetchBankTransactionList,
  fetchBankTransactionReport,
  IBankTransaction,
  IBankTransactionResponse
} from '../../../libraries/bank-transactions';
import { AppState } from '../../client-redux/reducers';
import { Language } from '../../languages/languageHandler';
import {
  formatCurrAmount,
  formatTransactionStatus,
  getLocationName,
  formatShortTransactionType
} from '../../utils/script';
import FilterBar, { FilterBarFieldsData } from '../../components/filterbar/index';
import PaginationComponent from '../../components/pagination';
import { fetchLog } from '../../../libraries/transactions';
import ShowBankTransaction from './show-bank-transaction-details';
import { ICurrency } from '../../../libraries/currencies';
import { LoginState } from '../../client-redux/login/reducer';
import { useFitTableRows } from '../../components/util/fit-table-rows-hook';
import { formatDateTimeNSecLabel, formatDateToISO } from '../../utils/date';
import * as Icon from '@tabler/icons-react';
import './styles.scss';

const BankTransactions = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const currencies = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);
  const user = useSelector<AppState, LoginState>(state => state.login);
  const controller = new AbortController();

  const [loading, setLoading] = useState<boolean>(false);
  const [offset, setOffset] = useState<number>(0);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: -2,
    minHeightForDynamic: 400
  });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalRowsResponse, setTotalRowsResponse] = useState<number | undefined>();
  const [list, setList] = useState<IBankTransactionResponse>();
  const [noResults, setNoResults] = useState<boolean>(false);
  const [openDetails, setOpenDetails] = useState<boolean>(false);
  const [selectedTransaction, setSelectedTransaction] = useState<IBankTransaction>();
  const [selectedTransactionCurrency, setSelectedTransactionCurrency] = useState<ICurrency>();
  const [statusOperCode, setStatusOperCode] = useState<number>();

  const [filterBarData, setFilterBarData] = useState<FilterBarFieldsData>();

  useEffect(() => {
    if (filterBarData && limit) {
      loadBankTransactionList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: FilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const loadBankTransactionList = () => {
    if (loading) {
      controller.abort();
    }
    if (!filterBarData || !limit) {
      return;
    }
    setLoading(true);
    setList(undefined);
    const shouldFetchTotalRows = offset === 0;

    fetchBankTransactionList(
      controller,
      formatDateToISO(filterBarData.beginDateTime),
      formatDateToISO(filterBarData.endDateTime),
      filterBarData.entity || user.user.entid || 0,
      filterBarData.lid || 0,
      offset,
      limit,
      shouldFetchTotalRows,
      filterBarData.uid
    )
      .then(response => {
        if (response.statusOper.code === 0) {
          setList(response);
          if (shouldFetchTotalRows && typeof response.totalRows === 'number') {
            setTotalRowsResponse(response.totalRows);
          }
          setNoResults(false);
          setStatusOperCode(response.statusOper.code);
        } else {
          setNoResults(true);
          setStatusOperCode(response.statusOper.code);
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') {
        } else {
        }
      })
      .finally(() => setLoading(false));
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  const getBankTransactionsReport = (format: string, eid: number, period: number, code: number) => {
    if (!format || !eid || !period) return;
    return fetchBankTransactionReport(format, eid, period, code, controller);
  };

  // ----------------- Modal information with logs -------------------//
  const showTransactionDetails = (n: number) => {
    if (list) {
      const transactionToShow = list.transactions[n];
      setSelectedTransaction(transactionToShow);
      setSelectedTransactionCurrency(currencies.find(currency => currency.cid === transactionToShow.cid));
      setOpenDetails(true);
    }
  };

  const getLog = () => {
    if (!selectedTransaction) return;
    return fetchLog(controller, selectedTransaction.eid, selectedTransaction.period, selectedTransaction.code);
  };
  //-------------------------------------------------------------//

  return (
    <div className="bankTransactions">
      {openDetails || selectedTransaction || selectedTransactionCurrency ? (
        <ShowBankTransaction
          transaction={selectedTransaction}
          openDetails={openDetails}
          hideBtn={() => setOpenDetails(false)}
          lang={lang}
          currencies={currencies}
          getLog={() => getLog()}
        />
      ) : null}

      <Header pageTitle={lang.BANK_TRANSACTIONS} />

      <FilterBar
        onSearch={loadBankTransactionList}
        onDataChange={handleFilterBarOnDataChange}
        fieldsUsage={{
          entity: true,
          lid: true,
          beginDateTime: true,
          endDateTime: true
        }}
      />

      <Table.ScrollContainer minWidth={500} ref={tableRef}>
        <Table striped highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{lang.EQUIPMENT_LOCAL}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_ID}</Table.Th>
              <Table.Th>{lang.PROCESSED_GLASS_PERIOD}</Table.Th>
              <Table.Th>{lang.GLOBAL_CODE}</Table.Th>
              <Table.Th>{lang.TRANSACTION_DEPOSITOR}</Table.Th>
              <Table.Th>{lang.GLOBAL_ACCOUNT}</Table.Th>
              <Table.Th>{lang.GLOBAL_DATE_TIME}</Table.Th>
              <Table.Th>{lang.GLOBAL_TYPE}</Table.Th>
              <Table.Th>{lang.GLOBAL_AMOUNT}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_COM_STATE}</Table.Th>
              <Table.Th>{lang.TRANSACTION_EXTERNAL_ID}</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={16}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : (
              <>
                {noResults ? (
                  <Table.Tr>
                    <Table.Td colSpan={16} style={{ textAlign: 'center' }}>
                      {lang.GLOBAL_NO_RESULTS}
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  list?.transactions.map((transaction, index) => {
                    return (
                      <Table.Tr key={index} onClick={() => showTransactionDetails(index)}>
                        <Table.Td>{getLocationName(transaction.sid)}</Table.Td>
                        <Table.Td>{transaction.eid}</Table.Td>
                        <Table.Td>{transaction.period}</Table.Td>
                        <Table.Td>{transaction.code}</Table.Td>
                        <Table.Td>{transaction.depositor}</Table.Td>
                        <Table.Td>{transaction.account}</Table.Td>
                        <Table.Td>{formatDateTimeNSecLabel(transaction.dateTime)}</Table.Td>
                        <Table.Td>{formatShortTransactionType(lang, transaction.type)}</Table.Td>
                        <Table.Td>{formatCurrAmount(transaction.value || 0, currencies[0])}</Table.Td>
                        <Table.Td>{formatTransactionStatus(lang, transaction.status)}</Table.Td>
                        <Table.Td>{transaction.hostTransactionID}</Table.Td>
                        <Table.Td
                          onClick={() =>
                            getBankTransactionsReport('pdf', transaction.eid, transaction.period, transaction.code)
                          }
                        >
                          <Icon.IconFileTypePdf stroke={2} />
                        </Table.Td>
                      </Table.Tr>
                    );
                  })
                )}
              </>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Group>
        <PaginationComponent
          total={totalRowsResponse}
          statusOperCode={statusOperCode}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>
    </div>
  );
};

export default BankTransactions;
